import React, {
    forwardRef,
    FC,
    ReactNode,
    useLayoutEffect,
    useRef,
    ReactElement,
    HTMLAttributes, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Portal from '../../layout/Portal/Portal';
import TagWrapper from '../TagWrapper';
import useEventListener from '../../hooks/useEventListener';
import { TModalFullScreen, TModalSize } from '../../type/modal-type';

interface IModalTitleProps extends HTMLAttributes<HTMLElement> {
    id: string;
    children: ReactNode;
    className?: string;
    tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
}
export const ModalTitle = forwardRef<HTMLHeadingElement, IModalTitleProps>(
    ({ tag = 'h6', id, children, className, ...props }, ref) => {
        return (
            <TagWrapper
                tag={tag}
                ref={ref}
                id={id}
                className={classNames('modal-title', className)}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}>
                {children}
            </TagWrapper>
        );
    },
);
ModalTitle.displayName = 'ModalTitle';

interface IModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactElement<IModalTitleProps> | ReactNode;
    className?: string;
    setIsOpen?(...args: unknown[]): unknown | undefined;
}
export const ModalHeader = forwardRef<HTMLDivElement, IModalHeaderProps>(
    ({ children, className, setIsOpen, ...props }, ref) => {
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={ref} className={classNames('modal-header', className)} {...props}>
                {children}
                {setIsOpen && (
                    <button
                        type='button'
                        className='btn-close'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                        onClick={() => setIsOpen(false)}
                    />
                )}
            </div>
        );
    },
);
ModalHeader.displayName = 'ModalHeader';
ModalHeader.defaultProps = {
    className: undefined,
    setIsOpen: undefined,
};

interface IModalBodyProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    className?: string;
}
export const ModalBody = forwardRef<HTMLDivElement, IModalBodyProps>(
    ({ children, className, ...props }, ref) => {
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={ref} className={classNames('modal-body', className)} {...props}>
                {children}
            </div>
        );
    },
);
ModalBody.displayName = 'ModalBody';
ModalBody.defaultProps = {
    className: undefined,
};

interface IModalFooterProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    className?: string;
}
export const ModalFooter = forwardRef<HTMLDivElement, IModalFooterProps>(
    ({ children, className, ...props }, ref) => {
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={ref} className={classNames('modal-footer', className)} {...props}>
                {children}
            </div>
        );
    },
);
ModalFooter.displayName = 'ModalFooter';
ModalFooter.defaultProps = {
    className: undefined,
};

interface IModalProps extends Record<string, any> {
    children:
        | ReactElement<IModalHeaderProps>[]
        | ReactElement<IModalBodyProps>[]
        | ReactElement<IModalFooterProps>[];
    isOpen: boolean;
    setIsOpen(...args: unknown[]): unknown;
    id?: string | undefined;
    titleId?: string;
    isStaticBackdrop?: boolean;
    isScrollable?: boolean;
    isCentered?: boolean;
    size?: TModalSize;
    fullScreen?: TModalFullScreen;
    isAnimation?: boolean;
}
const Modal: FC<IModalProps> = ({
                                    children,
                                    isOpen,
                                    setIsOpen,
                                    id,
                                    titleId,
                                    isStaticBackdrop,
                                    isScrollable,
                                    isCentered,
                                    size,
                                    fullScreen,
                                    isAnimation,
                                    ...props
                                }) => {
    const refModal = useRef(null);
    const ref = useRef(null);

    useEffect(() => {
        const closeModal = (event: MouseEvent | TouchEvent) => {
            if (
                refModal.current &&
                (refModal.current as HTMLDivElement).classList &&
                !(refModal.current as HTMLDivElement).contains(event.target as Node) &&
                !isStaticBackdrop
            ) {
                setIsOpen(false);
            }
        };

        const modalStatic = (event: MouseEvent | TouchEvent) => {
            if (
                refModal.current &&
                (refModal.current as HTMLDivElement).classList &&
                !(refModal.current as HTMLDivElement).contains(event.target as Node) &&
                isStaticBackdrop
            ) {
                (refModal.current as HTMLDivElement).classList.add('modal-static');
                setTimeout(() => (refModal.current as unknown as HTMLDivElement).classList.remove('modal-static'), 100);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', closeModal);
            document.addEventListener('touchstart', closeModal);
            document.addEventListener('mousedown', modalStatic);
            document.addEventListener('touchstart', modalStatic);
        }

        return () => {
            document.removeEventListener('mousedown', closeModal);
            document.removeEventListener('touchstart', closeModal);
            document.removeEventListener('mousedown', modalStatic);
            document.removeEventListener('touchstart', modalStatic);
        };
    }, [isOpen, isStaticBackdrop, setIsOpen]);

    // Keypress close function
    const escFunction = (event: { key: string }) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };
    useEventListener('keydown', escFunction);

    const ANIMATION_PROPS = isAnimation
        ? {
            initial: { opacity: 0, y: '-50%' },
            animate: { opacity: 1, x: '0%', y: '0%' },
            exit: { opacity: 0, y: '-50%' },
            transition: { ease: 'easeInOut', duration: 0.3 },
        }
        : null;

    return (
        <Portal>
            <AnimatePresence mode={'wait'}>
                {isOpen && (
                    <>
                        {/*@ts-ignore*/}
                        <motion.div
                            ref={refModal}
                            key='modal'
                            className={classNames('modal', { fade: isAnimation }, 'show')}
                            role='dialog'
                            style={{ display: 'block' }}
                            id={id}
                            tabIndex={-1}
                            aria-labelledby={titleId}
                            aria-hidden='true'
                            data-bs-backdrop={isStaticBackdrop ? 'static' : null}
                            data-bs-keyboard={isStaticBackdrop ? 'false' : null}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...ANIMATION_PROPS}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...props}>
                            <div
                                ref={ref}
                                className={classNames('modal-dialog', {
                                    'modal-dialog-scrollable': isScrollable,
                                    'modal-dialog-centered': isCentered,
                                    [`modal-${size}`]: size,
                                    [`modal-fullscreen${
                                        typeof fullScreen === 'string' ? `-${fullScreen}-down` : ''
                                    }`]: fullScreen,
                                })}>
                                <div className='modal-content'>{children}</div>
                            </div>
                        </motion.div>
                        <div
                            className={classNames('modal-backdrop', { fade: isAnimation }, 'show')}
                        />
                    </>
                )}
            </AnimatePresence>
        </Portal>
    );
};
Modal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    /**
     * ModalHeader, ModalBody and ModalFooter
     */
    // @ts-ignore
    children: PropTypes.node.isRequired,
    id: PropTypes.string,
    /**
     * For Accessibility
     */
    titleId: PropTypes.string,
    /**
     * When backdrop is set to static, the modal will not close when clicking outside it.
     */
    isStaticBackdrop: PropTypes.bool,
    /**
     * When modals become too long for the user’s viewport or device, they scroll independent of the page itself.
     */
    isScrollable: PropTypes.bool,
    /**
     * vertically center the modal
     */
    isCentered: PropTypes.bool,
    /**
     *  These sizes kick in at certain breakpoints to avoid horizontal scrollbars on narrower viewports.
     */
    size: PropTypes.oneOf([null, 'sm', 'lg', 'xl']),
    /**
     * Another override is the option to pop up a modal that covers the user viewport.
     */
    // @ts-ignore
    fullScreen: PropTypes.oneOfType([
        PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl']),
        PropTypes.bool,
    ]),
    isAnimation: PropTypes.bool,
};
Modal.defaultProps = {
    id: undefined,
    isStaticBackdrop: false,
    isScrollable: false,
    isCentered: false,
    size: null,
    fullScreen: false,
    isAnimation: true,
    titleId: undefined,
};

export default Modal;
